import { memo } from 'react';
import type { BillDTO } from '@six-pockets/shared';
import { EyeIcon } from '@/components/icons';
import { Button, StatusPill } from '@/components/ui';
import { cx } from '@/utils/cx';
import { toDateKey } from '@/utils/date';
import { formatDateTime, formatINR, formatPhone } from '@/utils/format';
import { BILL_STATUS, billContents, billParty } from '../utils';
import styles from '@/styles/components/BillRow/style.scss';

const shortDay = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/**
 * Money that came in on a later day than the bill (a balance collected): "paid 7 Oct" when all of it did, or
 * "+₹200 on 7 Oct" when part was paid at billing. Null when everything was paid on the bill's own day.
 */
function laterPaymentNote(bill: BillDTO): string | null {
  const billDay = toDateKey(new Date(bill.createdAt));
  const later = bill.payments.filter((p) => toDateKey(new Date(p.paidAt)) !== billDay);
  if (!later.length) return null;
  const days = [...new Set(later.map((p) => shortDay(p.paidAt)))].join(', ');
  const amount = later.reduce((a, p) => a + p.amount, 0);
  return amount === bill.paidAmount ? `paid ${days}` : `+${formatINR(amount)} on ${days}`;
}

/** A labelled amount; "—" when it is 0. An optional note sits under it. */
function Figure({ label, value, tone, note, className }: { label: string; value: number; tone: string | undefined; note?: string | null; className?: string }) {
  return (
    <span className={cx(styles.figure, className)}>
      <span className={styles.figureLabel}>{label}</span>
      {value > 0 ? (
        <span className={cx(styles.figureValue, tone)}>{formatINR(value)}</span>
      ) : (
        <span className={cx(styles.figureValue, styles.none)}>—</span>
      )}
      {note && <span className={styles.figureNote}>{note}</span>}
    </span>
  );
}

interface BillRowProps {
  bill: BillDTO;
  onView: (bill: BillDTO) => void;
}

/**
 * One bill on Bills home: number · billed to (with what was on it) · status · date · discount · paid · due · total ·
 * View. The figures are labelled cells of fixed width, so they line up down the list ("—" when nothing). Wraps onto
 * several lines on narrow screens.
 */
export const BillRow = memo(function BillRow({ bill, onView }: BillRowProps) {
  const status = BILL_STATUS[bill.status];

  return (
    <li className={styles.row}>
      <span className={styles.number}>#{bill.billNumber}</span>
      <div className={styles.main}>
        <div className={styles.titleRow}>
          <span className={cx(styles.party, !bill.customer && styles.noCustomer)}>{billParty(bill)}</span>
          {bill.customer && <span className={styles.phone}>{formatPhone(bill.customer.phone)}</span>}
        </div>
        <span className={styles.contents}>{billContents(bill)}</span>
      </div>
      <StatusPill tone={status.tone} label={status.label} />
      <span className={styles.when}>{formatDateTime(bill.createdAt)}</span>
      <div className={styles.figures}>
        <Figure label="Discount" value={bill.discount} tone={styles.discount} />
        <Figure
          label="Paid"
          value={bill.paidAmount}
          tone={styles.paid}
          // A balance waived at Pay now isn't money paid, but it explains why Paid + Due is less than the total.
          note={[laterPaymentNote(bill), bill.waived > 0 && `${formatINR(bill.waived)} waived`].filter(Boolean).join(' · ') || null}
          className={styles.paidCell}
        />
        <Figure label="Due" value={bill.balanceDue} tone={styles.due} />
        <span className={cx(styles.figure, styles.totalCell)}>
          <span className={styles.figureLabel}>Total</span>
          <span className={styles.total}>{formatINR(bill.total)}</span>
        </span>
      </div>
      <Button size="sm" variant="secondary" icon={<EyeIcon size={15} />} onClick={() => onView(bill)} aria-label={`View bill ${bill.billNumber}`}>
        View
      </Button>
    </li>
  );
});
